import { useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  useGetCurrentUserLazyQuery,
  useGetMyUpcomingBookingsWithAdventuresAndListingLazyQuery,
  useGetMyBucketListsLazyQuery,
  useGetBookingAdventuresIndexLazyQuery,
  useGetBucketListItemsIndexLazyQuery,
} from 'hooks/useQueries'
import { sendNotification } from 'actions/notificationActions'

import RiparideLogo from 'components/icons/BrandLogoPositive'
import InvisibleButton from 'components/display/InvisibleButton'
import { HeadingLg } from 'components/display/Typography'
import DialogModal from 'components/display/DialogModal'
import Overlay from 'components/display/Overlay'
import Spinner from 'components/display/Spinner'
import { FormContainer, Header, NavItem } from '../styles'
import LoginForm from '../LoginForm'
import SignupForm from '../SignupForm'
import ConfirmEmailForm from './ConfirmEmailForm'
import ResetPasswordForm from './ResetPasswordForm'

interface AuthProps {
  loading: boolean
  actionToReplay: any
}
interface ModalProps {
  auth: AuthProps
  initialRoute?: 'signup' | 'login' | 'confirm' | 'reset_password'
  onClose: () => any
  open?: boolean
  disableClose?: boolean
  dispatch: (arg0: object) => any
  serverSide?: boolean
  referredById?: string
  referralType?: string
}

const Modal = ({
  auth,
  initialRoute = 'signup',
  onClose,
  open,
  disableClose,
  dispatch,
  serverSide = false,
  referredById,
  referralType,
}: ModalProps) => {
  const [route, setRoute] = useState(initialRoute)
  const history = useHistory<{ from: string }>()

  const [loadCurrentUser] = useGetCurrentUserLazyQuery({
    // Important because `currentUser` in the rootquery will be null,
    // apollo will think that is a legit value, and decide to not update it
    // when other queries that are scoped to rootquery are called.
    fetchPolicy: 'network-only',
  })

  const [loadBucketLists] = useGetMyBucketListsLazyQuery({
    fetchPolicy: 'network-only',
  })

  const [loadBucketListItemsIndex] = useGetBucketListItemsIndexLazyQuery({
    fetchPolicy: 'network-only',
  })

  const [loadBookingAdventuresIndex] = useGetBookingAdventuresIndexLazyQuery({
    fetchPolicy: 'network-only',
  })

  const [
    loadUpcomingBookings,
  ] = useGetMyUpcomingBookingsWithAdventuresAndListingLazyQuery({
    fetchPolicy: 'network-only',
  })

  const handleConfirmEmail = () => setRoute('confirm')

  const handleResetPassword = () => setRoute('reset_password')

  const handleLoginSuccess = () => {
    if (auth.actionToReplay) {
      dispatch(auth.actionToReplay)
    }
    onClose()
    loadCurrentUser()
    loadBucketListItemsIndex()
    loadBucketLists()
    loadBookingAdventuresIndex()
    loadUpcomingBookings()

    dispatch(sendNotification('Signed in successfully.'))
  }

  const handleResetPasswordSuccess = () => {
    history.push('/login')
    dispatch(
      sendNotification(
        'You will receive an email with instructions on how to reset your password in a few minutes.'
      )
    )
  }

  return (
    <DialogModal
      onRequestClose={onClose}
      closeButtonColour="-ds-neutral-700"
      isOpen={open}
      smallModal
      rounded
      disableClose={disableClose || route === 'confirm'}
    >
      <Header>
        {(route === 'signup' || route === 'login') && (
          <>
            <RiparideLogo />
            <ul>
              <NavItem active={route === 'signup'}>
                <InvisibleButton
                  onClick={() => setRoute('signup')}
                  className="qa-sign-up-link"
                  aria-label="Show sign up form"
                >
                  Sign up
                </InvisibleButton>
              </NavItem>

              <NavItem active={route === 'login'}>
                <InvisibleButton
                  onClick={() => setRoute('login')}
                  className="qa-login-link"
                  aria-label="Show login form"
                >
                  Login
                </InvisibleButton>
              </NavItem>
            </ul>
          </>
        )}

        {route === 'confirm' && (
          <HeadingLg centered>Confirm your details</HeadingLg>
        )}
      </Header>

      <FormContainer>
        {route === 'signup' && (
          <SignupForm
            onSuccess={handleLoginSuccess}
            onConfirmEmail={handleConfirmEmail}
            serverSide={serverSide}
            referredById={referredById}
            referralType={referralType}
          />
        )}

        {route === 'login' && (
          <LoginForm
            onSuccess={handleLoginSuccess}
            onConfirmEmail={handleConfirmEmail}
            onResetPassword={handleResetPassword}
            serverSide={serverSide}
          />
        )}

        {route === 'confirm' && (
          <ConfirmEmailForm onSuccess={handleLoginSuccess} />
        )}

        {route === 'reset_password' && (
          <ResetPasswordForm onSuccess={handleResetPasswordSuccess} />
        )}
      </FormContainer>

      {auth.loading && (
        <Overlay>
          <Spinner />
        </Overlay>
      )}
    </DialogModal>
  )
}

export default connect(({ auth }: { auth: AuthProps }) => ({
  auth,
}))(Modal)
